import { useTranslation } from 'react-i18next';
import { Col, Container, Form, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { usePaymentHubDebtsLoader } from '../loaders/PaymentHubDebtsData';
import { Controller, useForm } from 'react-hook-form';
import IdentificationSearchFilter from '../../../../components/IdentificationSearchFilter';
import { DebtsDataFields } from '../types';
import { Button } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';

const IdentificationFilter = () => {
  const { t } = useTranslation(TranslationsKeys.PAYMENT_HUB_DEBTS);
  const { loading, updateQuery, refresh, query } = usePaymentHubDebtsLoader();
  const { control, watch, reset, handleSubmit } = useForm<DebtsDataFields>({
    defaultValues: { identification: undefined },
  });

  const identificationValue = watch('identification');

  const isQueryDisabled = useMemo(
    () =>
      !identificationValue ||
      !Object.keys(identificationValue).length ||
      !Object.keys(identificationValue).length ||
      Object.values(identificationValue).some((e) => !e?.trim()),
    [identificationValue],
  );

  const handleSearch = useCallback(
    (values: DebtsDataFields) => {
      if (!values?.identification || isQueryDisabled) {
        return;
      }

      const isDirty =
        values.identification?.identificationDocument !==
          query?.identificationDocument ||
        values.identification?.identificationDocumentType !==
          query?.identificationDocumentType ||
        values.identification?.identificationDocumentCountry !==
          query?.identificationDocumentCountry;

      if (isDirty) {
        updateQuery(values.identification);
      } else {
        refresh();
      }
    },
    [updateQuery, isQueryDisabled, refresh, query],
  );

  const handleClear = useCallback(() => {
    updateQuery({});
    reset({ identification: {} });
  }, [updateQuery, reset]);

  return (
    <Form onSubmit={handleSubmit(handleSearch)}>
      <Container fluid>
        <Row className="pb-4 gutters-filters">
          <Col xs={12}>
            <div className="d-flex justify-content-start flex-wrap">
              <div>
                <Controller
                  control={control}
                  name="identification"
                  render={({ field }) => (
                    <IdentificationSearchFilter
                      variant="outlined"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="d-flex align-items-center ml-2 mt-2">
                <Button
                  text={t('common:actions.search')}
                  size="md"
                  type="submit"
                  disabled={isQueryDisabled || loading}
                />
                <Button
                  text={t('common:actions.clean')}
                  className="ml-2"
                  size="md"
                  outlined
                  type="button"
                  disabled={loading}
                  onClick={handleClear}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </Form>
  );
};

export default IdentificationFilter;
